/**
 * passwordPolicy — the single definition of what makes a password acceptable.
 *
 * WHY THIS EXISTS (S-6, QA security review 2026-08-30)
 * ---------------------------------------------------
 * There were three different password policies in one product, and the server
 * held a fourth, weaker one:
 *
 *   SignUpPage          10 chars + lower + upper + digit
 *   SignInPage          10 chars + lower + upper + digit   (byte-identical copy)
 *   SettingsSecurityPage 8 chars, no character classes     (the outlier)
 *   GoTrue (prod)        6 chars, no character classes     (the real floor)
 *
 * So the rule a customer met depended on which screen they happened to be on,
 * and anyone calling the API directly met none of them. The two page-level
 * copies being byte-identical is what made the drift invisible: they agreed,
 * so nothing looked wrong, while a third screen quietly disagreed with both.
 *
 * This is the same defect shape as AR-3 (two copies of the cron-liveness
 * predicate) and the brand tokens in §4.2 — one rule, several copies, drift.
 * The fix is one copy, imported everywhere, with the server enforcing the same
 * floor so the UI is a courtesy rather than the only control.
 *
 * PAIRED WITH A SERVER CHANGE. `password_min_length` was raised 6 -> 10 in the
 * Supabase dashboard on 2026-08-30, and supabase/config.toml updated to match
 * (auth config is dashboard-owned — change both, or `pnpm auth:check` fails).
 * Keep MIN_LENGTH below in step with it: if they diverge, either the UI rejects
 * passwords the server would take, or the server rejects passwords the UI
 * promised were fine, and the second one shows the customer a raw GoTrue error.
 *
 * Safe to raise the server floor because NO server-side path sets a password:
 * every account-creating Edge Function (guest-order-initiate,
 * sales-order-create, staff-invite, lead-create, customer-import-existing)
 * creates the auth user with NO password and emails a set-password link. Only
 * these three screens ever set one.
 */

/** Must equal `password_min_length` in the Supabase dashboard + config.toml. */
export const PASSWORD_MIN_LENGTH = 10;

/**
 * Returns a human-readable problem, or null when the password is acceptable.
 *
 * Order matters: length first, then character classes, then the confirmation
 * match — so a customer fixes one thing at a time rather than being told their
 * passwords do not match while the password is also too short.
 */
export function validatePassword(pw: string, confirm: string): string | null {
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (!/[a-z]/.test(pw)) return 'Password must include a lowercase letter.';
  if (!/[A-Z]/.test(pw)) return 'Password must include an uppercase letter.';
  if (!/[0-9]/.test(pw)) return 'Password must include a digit.';
  if (pw !== confirm) return 'Passwords do not match.';
  return null;
}
